import React, { useEffect, useState } from 'react';
import { FaChevronLeft, FaCheck } from 'react-icons/fa6';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserView, MobileView } from 'react-device-detect';
import { orderDetails } from '../services/User-service';
import Footer from '../components/footer';
import Loader from '../components/Loader';
import { formatPrice } from '../utils/formatPrice';
import { logout } from '../Redux/features/User/userSlice';

const ORDER_STATUS_STEPS = [
    { key: 'placed', label: 'PLACED', shortLabel: 'PLACED' },
    { key: 'confirmed', label: 'ORDER CONFIRMED', shortLabel: 'CONFIRMED' },
    { key: 'packed', label: 'PACKED', shortLabel: 'PACKED' },
    { key: 'dispatched', label: 'DISPATCHED', shortLabel: 'DISPATCHED' },
    { key: 'delivered', label: 'DELIVERED', shortLabel: 'DELIVERED' },
];

const CANCELLED_STATUS_STEPS = [
    { key: 'cancelled', label: 'CANCELLED' },
];

function getOrderStatusRaw(order) {
    return String(
        order?.tracking?.current_status ||
        order?.tracking?.status ||
        order?.order_status ||
        order?.status ||
        'placed'
    ).toLowerCase().trim();
}

function isOrderCancelled(order) {
    const raw = getOrderStatusRaw(order);
    return raw === 'cancelled' || raw === 'canceled' || raw === 'order cancelled' || raw === 'order canceled';
}

function formatDetailDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
        });
    }
    return String(value).replace(/(\d+)(st|nd|rd|th)/i, '$1');
}

function getProductImage(product) {
    return product?.product_thumbail || product?.cover_image || product?.image || '';
}

function getPaymentMode(order) {
    if (order?.payment_method) {
        return String(order.payment_method).replace(/_/g, ' ').toUpperCase();
    }
    if (order?.payment_id) return 'ONLINE PAYMENT';
    return 'CASH ON DELIVERY';
}

function getActiveStatusIndex(order) {
    if (isOrderCancelled(order)) return 0;

    const raw = getOrderStatusRaw(order);

    const aliases = {
        placed: 0,
        pending: 0,
        confirmed: 1,
        order_confirmed: 1,
        'order confirmed': 1,
        production: 1,
        in_production: 1,
        'in production': 1,
        product_received: 1,
        received: 1,
        'product received': 1,
        qc: 1,
        qc_pass: 1,
        'qc pass': 1,
        packed: 2,
        dispatched: 3,
        shipped: 3,
        delivered: 4,
        completed: 4,
    };

    if (aliases[raw] != null) return aliases[raw];

    const matchIndex = ORDER_STATUS_STEPS.findIndex(
        (step) => step.key === raw || step.label.toLowerCase() === raw
    );
    return matchIndex >= 0 ? matchIndex : 0;
}

function formatAddressBlock(address) {
    if (!address) return null;

    const name = [address.first_name, address.last_name].filter(Boolean).join(' ').trim();
    const lines = [
        address.address || address.address1,
        address.address2,
        [address.city, address.state].filter(Boolean).join(', '),
        [address.country, address.zip_code || address.post_code].filter(Boolean).join(' - '),
        address.phone ? String(address.phone) : null,
    ].filter(Boolean);

    return { name, lines };
}

function AccountSidebar({ onLogout }) {
    return (
        <div className="h5 optionListLineHeight xs3 layout column orat-dark-grey-color">
            <Link to="/myaccount" className="cursor-pointer demi-bold orat-black-color"> PERSONAL DETAILS </Link>
            <Link to="/orderhistory" className="cursor-pointer demi-bold orat-color"> ORDER HISTORY </Link>
            <Link to="/addaddress" className="cursor-pointer demi-bold orat-black-color"> MANAGE ADDRESSES </Link>
            <Link to="/wishlist" className="cursor-pointer demi-bold orat-black-color"> MY WISHLIST </Link>
            <Link onClick={onLogout} className="cursor-pointer demi-bold orat-black-color"> LOGOUT </Link>
        </div>
    );
}

function OrderStatusTracker({ activeIndex, cancelled = false }) {
    const steps = cancelled ? CANCELLED_STATUS_STEPS : ORDER_STATUS_STEPS;

    return (
        <div className={`OrderDetailsTracker${cancelled ? ' is-cancelled' : ''}`}>
            {!cancelled ? (
                <div className="OrderDetailsTrackerLine">
                    <div
                        className="OrderDetailsTrackerLineFill"
                        style={{
                            width: activeIndex <= 0
                                ? '0%'
                                : `${(activeIndex / (steps.length - 1)) * 100}%`,
                        }}
                    />
                </div>
            ) : null}
            <div className="OrderDetailsTrackerSteps">
                {steps.map((step, index) => {
                    const isDone = !cancelled && index <= activeIndex;
                    const isCurrent = cancelled || index === activeIndex;
                    return (
                        <div
                            key={step.key}
                            className={`OrderDetailsTrackerStep${isDone ? ' is-done' : ''}${isCurrent ? ' is-current' : ''}${cancelled ? ' is-cancelled' : ''}`}
                        >
                            {!cancelled ? (
                                <span className="OrderDetailsTrackerDot">
                                    {isDone ? <FaCheck size={8} /> : null}
                                </span>
                            ) : null}
                            <span className="OrderDetailsTrackerLabel">
                                <span className="OrderDetailsTrackerLabelFull">{step.label}</span>
                                <span className="OrderDetailsTrackerLabelShort">
                                    {step.shortLabel || step.label}
                                </span>
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function OrderDetailsContent({ order }) {
    const products = Array.isArray(order?.productslist) ? order.productslist : [];
    const shipping = formatAddressBlock(order?.shipping_address || order?.billing_address);
    const billing = formatAddressBlock(order?.billing_address || order?.shipping_address);
    const cancelled = isOrderCancelled(order);
    const activeIndex = getActiveStatusIndex(order);
    const orderValue = formatPrice(order?.total_amount, { showSymbol: false });

    return (
        <div className="OrderDetailsPage">
            <Link to="/orderhistory" className="OrderDetailsBack">
                <FaChevronLeft size={12} />
                <span>BACK TO ORDER HISTORY</span>
            </Link>

            <div className="OrderDetailsFrame">
                <div className="OrderDetailsSummary">
                    <div className="OrderDetailsSummaryCol OrderDetailsSummaryMeta">
                        <div className="OrderDetailsField">
                            <div className="OrderDetailsLabel">ORDER ID</div>
                            <div className="OrderDetailsValue">{order?.order_number}</div>
                        </div>
                        <div className="OrderDetailsField">
                            <div className="OrderDetailsLabel">ORDER VALUE</div>
                            <div className="OrderDetailsValue">₹{orderValue}</div>
                        </div>
                        <div className="OrderDetailsField">
                            <div className="OrderDetailsLabel">ORDER DATE</div>
                            <div className="OrderDetailsValue">{formatDetailDate(order?.created_at)}</div>
                        </div>
                        <div className="OrderDetailsField">
                            <div className="OrderDetailsLabel">PAYMENT MODE</div>
                            <div className="OrderDetailsValue">{getPaymentMode(order)}</div>
                        </div>
                    </div>

                    <div className="OrderDetailsSummaryCol">
                        <div className="OrderDetailsField">
                            <div className="OrderDetailsLabel">SHIPPING ADDRESS</div>
                            {shipping ? (
                                <div className="OrderDetailsAddress">
                                    {shipping.name ? <div className="OrderDetailsValue">{shipping.name}</div> : null}
                                    {shipping.lines.map((line) => (
                                        <div key={line} className="OrderDetailsAddressLine">{line}</div>
                                    ))}
                                </div>
                            ) : (
                                <div className="OrderDetailsValue muted">Address not available</div>
                            )}
                        </div>
                    </div>

                    <div className="OrderDetailsSummaryCol">
                        <div className="OrderDetailsField">
                            <div className="OrderDetailsLabel">BILLING ADDRESS</div>
                            {billing ? (
                                <div className="OrderDetailsAddress">
                                    {billing.name ? <div className="OrderDetailsValue">{billing.name}</div> : null}
                                    {billing.lines.map((line) => (
                                        <div key={`b-${line}`} className="OrderDetailsAddressLine">{line}</div>
                                    ))}
                                </div>
                            ) : (
                                <div className="OrderDetailsValue muted">Address not available</div>
                            )}
                        </div>
                    </div>
                </div>

                <OrderStatusTracker activeIndex={activeIndex} cancelled={cancelled} />

                <div className="OrderDetailsProducts">
                    {products.length === 0 ? (
                        <div className="OrderDetailsEmptyProducts">No products found for this order.</div>
                    ) : (
                        products.map((product, index) => (
                            <div
                                className="OrderDetailsProductRow"
                                key={`${order?.id || 'order'}-${product.product_id || product.id || index}`}
                            >
                                <Link
                                    to={`/products/productdetails/${product.product_id}`}
                                    className="OrderDetailsProductThumb"
                                >
                                    <img
                                        src={getProductImage(product)}
                                        alt={product.product_title || 'Product'}
                                    />
                                </Link>
                                <div className="OrderDetailsProductInfo">
                                    <div className="OrderDetailsProductBrand">
                                        {product.designer_name || product.brand_name || product.product_title}
                                    </div>
                                    {product.product_short_description ? (
                                        <div className="OrderDetailsProductName">
                                            {product.product_short_description}
                                        </div>
                                    ) : null}
                                    {product.sku ? (
                                        <div className="OrderDetailsProductMeta">CODE : {product.sku}</div>
                                    ) : null}
                                    {product.size ? (
                                        <div className="OrderDetailsProductMeta">SIZE : {product.size}</div>
                                    ) : null}
                                </div>
                                <div className="OrderDetailsProductPrice">
                                    ₹{formatPrice(
                                        product.saleprice > 0 ? product.saleprice : product.price,
                                        { showSymbol: false }
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

function OrderDetailsPageShell({ onLogout, children }) {
    return (
        <>
            <BrowserView>
                <div className="App other-page-top my-orderhistory-mobile my-orderdetails-page">
                    <div className="AppContent BreakPointContainer">
                        <div className="MyAccountWrapper prel">
                            <div className="MyAccBody layout xs12">
                                <AccountSidebar onLogout={onLogout} />
                                <div className="MyAccountPageWrapper flex xs9 prel">
                                    {children}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </BrowserView>
            <MobileView>
                <div className="App other-page-top my-orderhistory-mobile my-orderdetails-page">
                    <div className="AppContent BreakPointContainer container">
                        <div className="MyAccountWrapper prel">
                            <div className="MyAccBody">
                                <div className="MyAccountPageWrapper prel">
                                    {children}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </MobileView>
        </>
    );
}

function Orderdetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userInfo = useSelector((state) => state.user.userInfo);
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const handleLogout = () => {
        dispatch(logout(userInfo));
        navigate('/');
    };

    useEffect(() => {
        if (!userInfo) {
            navigate('/');
            return;
        }

        if (!id) {
            setError('Order not found');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError('');

        orderDetails({
            order_id: Number(id) || id,
            user_id: userInfo?.data?.user?.id,
        })
            .then((response) => {
                if (response?.success === false || !response?.data) {
                    setError(response?.message || 'Order not found');
                    setOrder(null);
                } else {
                    setOrder(response.data);
                }
            })
            .catch((err) => {
                console.error('Error fetching order details:', err);
                setError(err?.response?.data?.message || 'Unable to load order details');
                setOrder(null);
            })
            .finally(() => setLoading(false));
    }, [id, navigate, userInfo]);

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            <OrderDetailsPageShell onLogout={handleLogout}>
                {error || !order ? (
                    <div className="OrderDetailsPage">
                        <Link to="/orderhistory" className="OrderDetailsBack">
                            <FaChevronLeft size={12} />
                            <span>BACK TO ORDER HISTORY</span>
                        </Link>
                        <div className="OrderDetailsError">{error || 'Order not found'}</div>
                    </div>
                ) : (
                    <OrderDetailsContent order={order} />
                )}
            </OrderDetailsPageShell>
            <Footer />
        </>
    );
}

export default Orderdetails;
